import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useState } from "react";
import { Download } from "lucide-react";
import mermaid from "mermaid";
mermaid.initialize({
    startOnLoad: false,
    theme: "dark",
    securityLevel: "strict",
    gantt: { useMaxWidth: false },
    timeline: { useMaxWidth: false },
    mindmap: { useMaxWidth: false },
    quadrantChart: { useMaxWidth: false },
});
/** Module-level cache for rendered SVGs to prevent flickering on re-mount */
const svgCache = new Map();
/** Clear the SVG cache - exported for testing purposes */
export const clearSvgCache = () => svgCache.clear();
/** Convert SVG string to PNG blob */
export const svgToPng = async (svgString, scale = 2) => {
    // Parse SVG
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
    const svgElement = svgDoc.documentElement;
    // Ensure proper namespace for standalone SVG
    svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svgElement.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    // Get dimensions from viewBox (mermaid always sets viewBox)
    const viewBox = svgElement.getAttribute("viewBox");
    if (!viewBox) {
        console.error("SVG has no viewBox");
        return null;
    }
    const [, , vbWidth, vbHeight] = viewBox.split(" ").map(Number);
    const width = vbWidth || 800;
    const height = vbHeight || 600;
    // Set explicit pixel dimensions matching viewBox
    svgElement.setAttribute("width", String(width));
    svgElement.setAttribute("height", String(height));
    // Add background rect for dark theme (insert at beginning)
    const bgRect = svgDoc.createElementNS("http://www.w3.org/2000/svg", "rect");
    bgRect.setAttribute("x", "0");
    bgRect.setAttribute("y", "0");
    bgRect.setAttribute("width", String(width));
    bgRect.setAttribute("height", String(height));
    bgRect.setAttribute("fill", "#1e1e2e");
    svgElement.insertBefore(bgRect, svgElement.firstChild);
    // Serialize back to string
    const serializer = new XMLSerializer();
    const svgData = serializer.serializeToString(svgElement);
    // Create canvas with scaled dimensions for better quality
    const canvas = document.createElement("canvas");
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx)
        return null;
    // Scale context for high DPI
    ctx.scale(scale, scale);
    // Use base64 data URL (more compatible than blob URL)
    const base64 = btoa(unescape(encodeURIComponent(svgData)));
    const dataUrl = `data:image/svg+xml;base64,${base64}`;
    const img = new Image();
    return new Promise((resolve) => {
        img.onload = () => {
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => resolve(blob), "image/png");
        };
        img.onerror = (e) => {
            console.error("Failed to load SVG for PNG export:", e);
            resolve(null);
        };
        img.src = dataUrl;
    });
};
export const MermaidDiagram = ({ chart }) => {
    const internalId = useId();
    // Mermaid requires id to start with a letter, so prefix with 'm'
    const id = `m${internalId.replace(/:/g, "")}`;
    const cachedSvg = svgCache.get(chart);
    const [svg, setSvg] = useState(cachedSvg ?? "");
    useEffect(() => {
        const renderDiagram = async () => {
            if (!chart) {
                setSvg("");
                return;
            }
            const cached = svgCache.get(chart);
            if (cached) {
                setSvg(cached);
                return;
            }
            try {
                await mermaid.parse(chart);
                const { svg: renderedSvg } = await mermaid.render(id, chart);
                svgCache.set(chart, renderedSvg);
                setSvg(renderedSvg);
            }
            catch {
                // Clean up error div that mermaid creates
                const errorDiv = document.querySelector(`#d${id}`);
                errorDiv?.remove();
            }
        };
        renderDiagram();
    }, [chart, id]);
    const handleDownloadPng = useCallback(async () => {
        if (!svg)
            return;
        const blob = await svgToPng(svg);
        if (!blob)
            return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `mermaid-diagram-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [svg]);
    // Show diagram if we have SVG (from cache or render), otherwise show code block
    if (!svg) {
        return (_jsxs("div", { className: "min-h-48 rounded-lg border border-border/50 bg-zinc-900 overflow-hidden", children: [_jsx("div", { className: "px-4 py-2 text-xs text-zinc-400 bg-zinc-800 border-b border-border/50", children: "mermaid" }), _jsx("pre", { className: "p-4 overflow-x-auto text-sm text-zinc-300", children: _jsx("code", { children: chart }) })] }));
    }
    return (_jsxs("div", { className: "group relative min-h-48 flex items-center justify-center rounded-lg border border-border/50 bg-muted/20 overflow-x-auto", children: [_jsx("button", { type: "button", onClick: handleDownloadPng, className: "absolute top-2 right-2 p-1.5 rounded-md bg-zinc-800/80 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/80 opacity-0 group-hover:opacity-100 transition-opacity", title: "Download as PNG", children: _jsx(Download, { size: 16 }) }), _jsx("div", { className: "mermaid w-full", role: "img", "aria-label": `Mermaid diagram: ${chart.split("\n")[0]}`, dangerouslySetInnerHTML: { __html: svg } })] }));
};
//# sourceMappingURL=MermaidDiagram.js.map